import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Crown, ArrowRight, Gift, X, Shield, Award, Star, Diamond, Gem, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';

interface PreviousBonusInfo {
  alreadyClaimed: boolean;
  claimedAt: string | null;
  createdAt: string;
}

interface VipTierUpgrade {
  oldTier: {
    id: string;
    tier_name: string;
    tier_level: number;
    tier_color: string;
  };
  newTier: {
    id: string;
    tier_name: string;
    tier_level: number;
    tier_color: string;
    tier_reach_reward_gc: number;
    tier_reach_reward_sc: number;
  };
  bonusAwarded: boolean;
  previousBonusInfo?: PreviousBonusInfo | null;
  isManualUpgrade?: boolean;
}

interface VipTierUpgradeModalProps {
  upgrade: VipTierUpgrade | null;
  onClose: () => void;
}

const getTierIcon = (level: number) => {
  switch (level) {
    case 1: return Shield;
    case 2: return Award;
    case 3: return Star;
    case 4: return Crown;
    case 5: return Diamond;
    case 6: return Gem;
    case 7: return Diamond;
    case 8: return Crown;
    default: return Crown;
  }
};

export const VipTierUpgradeModal = ({ upgrade, onClose }: VipTierUpgradeModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayedUpgrade, setDisplayedUpgrade] = useState<VipTierUpgrade | null>(null);
  const [isClaiming, setIsClaiming] = useState(false);
  const [bonusClaimed, setBonusClaimed] = useState(false);

  useEffect(() => {
    try {
      if (upgrade && upgrade.newTier && upgrade.oldTier && upgrade.newTier.id) {
        if (!displayedUpgrade || upgrade.newTier.id !== displayedUpgrade.newTier?.id) {
          if (upgrade.oldTier.id && upgrade.oldTier.tier_name && upgrade.newTier.tier_name) {
            setDisplayedUpgrade(upgrade);
            setIsOpen(true);
            setBonusClaimed(false);
          }
        }
      }
    } catch {
      // Silently handle errors in upgrade modal
    }
  }, [upgrade]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setDisplayedUpgrade(null);
      setBonusClaimed(false);
      onClose();
    }, 300);
  };

  const handleClaimBonus = async () => {
    if (!displayedUpgrade?.newTier?.id || isClaiming) return;
    
    setIsClaiming(true);
    try {
      // Fetch custom rewards to find the tier reach bonus for this tier
      const customRewardsResponse = await apiClient.get('/loyalty/custom-rewards');
      const customRewards = customRewardsResponse.customRewards || [];
      
      const targetTierId = String(displayedUpgrade.newTier.id);
      
      // Find the unclaimed tier reach bonus for this specific tier
      const tierReachBonus = customRewards.find((reward: any) => {
        // Check if it's a vip_tier_reach reward type
        if (reward.reward_type !== 'vip_tier_reach') return false;
        if (reward.is_claimed) return false;
        
        // Parse metadata if it's a string
        const metadata = typeof reward.metadata === 'string' 
          ? JSON.parse(reward.metadata) 
          : reward.metadata;
        
        // Compare tier IDs as strings to handle type mismatches
        const rewardTierId = String(metadata?.tier_id || '');
        return rewardTierId === targetTierId;
      });

      if (!tierReachBonus) {
        // If no bonus found by tier_id, try to find any unclaimed vip_tier_reach bonus
        // This handles cases where the tier_id format might differ
        const anyTierReachBonus = customRewards.find((reward: any) => 
          reward.reward_type === 'vip_tier_reach' && !reward.is_claimed
        );
        
        if (anyTierReachBonus) {
          // Claim this one instead
          const claimResponse = await apiClient.post('/reward/claim', {
            rewardId: anyTierReachBonus.id,
            rewardType: 'custom'
          });

          if (!claimResponse?.success) {
            throw new Error(claimResponse?.error || 'Claim failed');
          }

          toast.success(`${displayedUpgrade.newTier.tier_name} tier bonus claimed!`);
          setBonusClaimed(true);
          setTimeout(() => handleClose(), 1500);
          return;
        }
        
        toast.error('Bonus not found or already claimed');
        setIsClaiming(false);
        return;
      }

      // Claim the reward
      const claimResponse = await apiClient.post('/reward/claim', {
        rewardId: tierReachBonus.id,
        rewardType: 'custom'
      });

      if (!claimResponse?.success) {
        throw new Error(claimResponse?.error || 'Claim failed');
      }

      toast.success(`${displayedUpgrade.newTier.tier_name} tier bonus claimed!`);
      setBonusClaimed(true);
      
      // Close modal after a short delay
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error: any) {
      toast.error(error.message || 'Failed to claim bonus');
    } finally {
      setIsClaiming(false);
    }
  };

  if (!displayedUpgrade) return null;
  if (!displayedUpgrade.oldTier || !displayedUpgrade.newTier) return null;
  if (!displayedUpgrade.oldTier.id || !displayedUpgrade.newTier.id) return null;
  if (!displayedUpgrade.oldTier.tier_name || !displayedUpgrade.newTier.tier_name) return null;

  const formatAmount = (amount: number | string | undefined) => {
    try {
      const numAmount = typeof amount === 'string' ? parseFloat(amount.replace(/,/g, '')) : (amount || 0);
      if (isNaN(numAmount)) return '0';
      return numAmount.toLocaleString(undefined, { maximumFractionDigits: 0 });
    } catch {
      return String(amount || 0);
    }
  };

  const newTierColor = displayedUpgrade.newTier?.tier_color || '#a855f7';
  const oldTierColor = displayedUpgrade.oldTier?.tier_color || '#666';
  const TierIcon = getTierIcon(displayedUpgrade.newTier?.tier_level || 1);
  
  const rewardGc = typeof displayedUpgrade.newTier?.tier_reach_reward_gc === 'string'
    ? parseFloat(String(displayedUpgrade.newTier.tier_reach_reward_gc).replace(/,/g, '')) || 0
    : (displayedUpgrade.newTier?.tier_reach_reward_gc || 0);
  
  const rewardSc = typeof displayedUpgrade.newTier?.tier_reach_reward_sc === 'string'
    ? parseFloat(String(displayedUpgrade.newTier.tier_reach_reward_sc).replace(/,/g, '')) || 0
    : (displayedUpgrade.newTier?.tier_reach_reward_sc || 0);

  // Show claim button if:
  // 1. Tier has rewards configured (rewardGc > 0 || rewardSc > 0), AND
  // 2. Bonus was NOT explicitly marked as already claimed, AND
  // 3. This is NOT a manual upgrade (manual upgrades don't get rewards)
  // We show the button by default if there are rewards - the claim handler will check if it's available
  const bonusAlreadyClaimed = displayedUpgrade.previousBonusInfo?.alreadyClaimed === true;
  const hasRewards = rewardGc > 0 || rewardSc > 0;
  const isManualUpgrade = displayedUpgrade.isManualUpgrade === true;
  const showClaimButton = hasRewards && !bonusAlreadyClaimed && !bonusClaimed && !isManualUpgrade;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-md w-[95vw] p-0 overflow-visible border-0 bg-transparent shadow-none [&>button]:hidden"
        aria-describedby={undefined}
      >
        {/* Outer container matching VIP tier card style */}
        <div 
          className="relative rounded-xl overflow-hidden"
          style={{
            boxShadow: `0 0 80px ${newTierColor}60, 0 0 120px ${newTierColor}30, 0 8px 40px rgba(0,0,0,0.5)`,
          }}
        >
          {/* Solid opaque background base */}
          <div className="absolute inset-0 bg-background" />
          
          {/* Card Background with gradient overlay - matching tier card */}
          <div 
            className="absolute inset-0"
            style={{
              background: `linear-gradient(145deg, ${newTierColor}40 0%, ${newTierColor}20 50%, transparent 100%)`,
            }}
          />
          
          {/* Decorative casino pattern */}
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <div className="absolute inset-0" style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, ${newTierColor} 10px, ${newTierColor} 11px)`,
            }} />
          </div>

          {/* Shine effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none" />

          {/* Radiating glow */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute -inset-3 rounded-2xl pointer-events-none"
            style={{ 
              background: `radial-gradient(ellipse at center, ${newTierColor}50 0%, ${newTierColor}20 40%, transparent 70%)`,
              filter: 'blur(15px)',
            }}
          />

          {/* Inner glow ring */}
          <div 
            className="absolute inset-1 rounded-xl pointer-events-none"
            style={{ 
              boxShadow: `inset 0 0 50px ${newTierColor}50`,
            }}
          />

          {/* Shimmer sweep */}
          <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
            <div 
              className="absolute inset-0"
              style={{
                background: `linear-gradient(110deg, transparent 20%, ${newTierColor}30 50%, transparent 80%)`,
                animation: 'shimmer 4s ease-in-out infinite',
              }}
            />
          </div>

          {/* Border */}
          <div 
            className="absolute inset-0 rounded-xl pointer-events-none"
            style={{
              border: `2px solid ${newTierColor}50`,
            }}
          />

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-50 w-8 h-8 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors border border-border"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>

          {/* Content */}
          <div className="relative z-10 p-6 sm:p-8">
            {/* Header with tier icon */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, type: "spring", bounce: 0.3 }}
              className="text-center mb-6"
            >
              {/* Tier Icon Badge */}
              <div 
                className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 shadow-lg"
                style={{ 
                  background: `linear-gradient(135deg, ${newTierColor}, ${newTierColor}80)`,
                  boxShadow: `0 4px 15px ${newTierColor}50`,
                }}
              >
                <TierIcon className="h-8 w-8 text-white drop-shadow-lg" />
              </div>
              
              <motion.h2 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.4 }}
                className="text-2xl sm:text-3xl font-black text-white mb-1 tracking-wide drop-shadow-md"
              >
                TIER UPGRADED
              </motion.h2>
              <motion.p 
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.4 }}
                className="text-sm"
                style={{ color: newTierColor }}
              >
                Welcome to {displayedUpgrade.newTier?.tier_name}
              </motion.p>
            </motion.div>

            {/* Tier Comparison */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="flex items-center justify-center gap-3 sm:gap-4 mb-6"
            >
              {/* Old Tier */}
              <div 
                className="flex flex-col items-center p-3 sm:p-4 rounded-xl min-w-[90px] sm:min-w-[100px]"
                style={{
                  background: `linear-gradient(145deg, ${oldTierColor}15, rgba(15,23,42,0.8))`,
                  border: `1px solid ${oldTierColor}30`,
                }}
              >
                <div className="text-[10px] text-muted-foreground/70 mb-1 uppercase tracking-wider">Previous</div>
                <div className="text-base sm:text-lg font-bold" style={{ color: oldTierColor }}>
                  {displayedUpgrade.oldTier?.tier_name || 'Unknown'}
                </div>
                <div className="text-[10px] text-muted-foreground/70">
                  Level {displayedUpgrade.oldTier?.tier_level ?? 0}
                </div>
              </div>

              {/* Arrow */}
              <motion.div
                animate={{ x: [0, 6, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <ArrowRight className="h-6 w-6" style={{ color: newTierColor }} />
              </motion.div>

              {/* New Tier */}
              <div 
                className="flex flex-col items-center p-3 sm:p-4 rounded-xl min-w-[90px] sm:min-w-[100px]"
                style={{
                  background: `linear-gradient(145deg, ${newTierColor}25, ${newTierColor}10)`,
                  border: `2px solid ${newTierColor}60`,
                  boxShadow: `0 0 20px ${newTierColor}30`,
                }}
              >
                <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: newTierColor }}>New Tier</div>
                <div className="text-lg sm:text-xl font-black" style={{ color: newTierColor }}>
                  {displayedUpgrade.newTier?.tier_name || 'Unknown'}
                </div>
                <div className="text-[10px]" style={{ color: `${newTierColor}aa` }}>
                  Level {displayedUpgrade.newTier?.tier_level ?? 0}
                </div>
              </div>
            </motion.div>

            {/* Bonus Information - show when bonus is available to claim or was just claimed (not for manual upgrades) */}
            {hasRewards && !bonusAlreadyClaimed && !isManualUpgrade && (
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.3 }}
                className="relative p-4 sm:p-5 rounded-xl mb-6"
                style={{
                  background: bonusClaimed 
                    ? 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(16,185,129,0.1))'
                    : `linear-gradient(135deg, ${newTierColor}15, rgba(16,185,129,0.1))`,
                  border: bonusClaimed 
                    ? '1px solid rgba(34,197,94,0.4)'
                    : `1px solid ${newTierColor}30`,
                }}
              >
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Gift className={`h-5 w-5 ${bonusClaimed ? 'text-emerald-400' : ''}`} style={{ color: bonusClaimed ? undefined : newTierColor }} />
                  <span className={`text-sm font-bold ${bonusClaimed ? 'text-emerald-400' : 'text-white'}`}>
                    {bonusClaimed ? 'Bonus Claimed!' : 'Tier Reach Bonus'}
                  </span>
                </div>
                <div className="flex justify-center gap-6 mb-2">
                  {rewardGc > 0 && (
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl font-black text-amber-400 drop-shadow-lg">
                        {formatAmount(rewardGc)}
                      </div>
                      <div className="text-[10px] text-amber-400/70 font-medium">GOLD COINS</div>
                    </div>
                  )}
                  {rewardSc > 0 && (
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl font-black text-emerald-400 drop-shadow-lg">
                        {formatAmount(rewardSc)}
                      </div>
                      <div className="text-[10px] text-emerald-400/70 font-medium">SWEEPS COINS</div>
                    </div>
                  )}
                </div>
                {bonusClaimed && (
                  <p className="text-[10px] text-center text-emerald-400">
                    Added to your balance
                  </p>
                )}
              </motion.div>
            )}

            {/* Already Claimed Bonus Info - only show when bonus was actually claimed */}
            {displayedUpgrade.previousBonusInfo?.alreadyClaimed && (
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.3 }}
                className="bg-emerald-500/10 p-4 rounded-xl border border-emerald-500/20 mb-6"
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Gift className="h-4 w-4 text-emerald-400" />
                  <span className="text-sm font-medium text-emerald-400">
                    Tier Bonus Already Claimed
                  </span>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  You claimed the {displayedUpgrade.newTier?.tier_name} tier bonus on{' '}
                  <span className="text-emerald-400 font-medium">
                    {new Date(displayedUpgrade.previousBonusInfo.claimedAt!).toLocaleDateString('en-US', {
                      month: '2-digit',
                      day: '2-digit',
                      year: 'numeric'
                    })}
                  </span>
                </p>
              </motion.div>
            )}

            {/* Action Buttons */}
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.4 }}
              className="flex gap-3"
            >
              {showClaimButton && (
                <Button 
                  onClick={handleClaimBonus}
                  disabled={isClaiming}
                  className="flex-1 h-12 font-bold text-white border-0 shadow-lg transition-all hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{
                    background: `linear-gradient(135deg, ${newTierColor}, ${newTierColor}cc)`,
                    boxShadow: `0 4px 20px ${newTierColor}40`,
                  }}
                  size="lg"
                >
                  {isClaiming ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Claiming...
                    </>
                  ) : (
                    'Claim Bonus'
                  )}
                </Button>
              )}
              <Button 
                onClick={handleClose}
                variant="outline"
                className={`h-12 bg-muted/80 border-border hover:bg-muted text-foreground font-semibold ${showClaimButton ? 'flex-1' : 'w-full'}`}
                size="lg"
              >
                {bonusClaimed ? 'Continue' : showClaimButton ? 'Claim Later' : 'Continue Playing'}
              </Button>
            </motion.div>
          </div>
        </div>

        {/* CSS for shimmer animation */}
        <style>{`
          @keyframes shimmer {
            0%, 100% { transform: translateX(-100%); }
            50% { transform: translateX(100%); }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};
